import * as React from 'react';

import type { BlockDrawingData } from '@gitbook/doc-core';
import {
    PortalAbsolute,
    useAnimateCSSLifecycle,
    useConfigurationTheme,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, LoadingPanel, toast } from '@gitbook/spine-react';
import { throwIfNotExposable } from '@gitbook/util-debug';
import { assertIsDefined } from '@gitbook/util-ts';

import { useContentReference } from '../../ContentReferences';
import { ContentEditorContext } from '../contexts';
import { CloseDrawingButton } from './CloseDrawingButton';
import {
    createDrawingFile,
    createDrawingSVG,
    type DrawingDocumentData,
    drawingDataCache,
} from './data';
import {
    type ExcalidrawElement,
    type ExcalidrawImperativeAPI,
    excalidrawSuspense,
} from './excalidraw';
import { GenerateDrawingToolbar, type GenerateDrawingToolbarRef } from './GenerateDrawingToolbar';

/**
 * Modal to edit the drawing file.
 */
export function DrawingEditModal(props: {
    nodeKey: string;
    blockData: BlockDrawingData;
    /** Callback when closing. If `file` is passed it means the document has been modified */
    onClose: (file: File | undefined) => void;
}): React.ReactElement {
    const { nodeKey, blockData, onClose } = props;

    const excalidrawRef = React.useRef<ExcalidrawImperativeAPI>();

    const animatedBackdropRef = useAnimateCSSLifecycle<HTMLDivElement>({
        mount: 'animate-in fade-in',
        unmount: 'animate-out fade-out',
    });
    const animatedBodyRef = useAnimateCSSLifecycle<HTMLDivElement>(
        {
            mount: 'animate-in fade-in zoom-in-90',
            unmount: 'animate-out fade-out zoom-out-90',
        },
        {
            onEnd: () => {
                // Refresh excalidraw when the modal is opened
                // to make sure that excalidraw uses the proper container position.
                excalidrawRef.current?.refresh();
            },
        }
    );

    return (
        <PortalAbsolute>
            <div
                ref={animatedBackdropRef}
                className="absolute inset-0 bg-[--color-backdrop-background]"
            >
                <div
                    ref={animatedBodyRef}
                    className="absolute inset-6 overflow-hidden rounded-md bg-[--color-background-main]"
                >
                    <React.Suspense fallback={<LoadingPanel size="large" />}>
                        <DrawingEditModalBody
                            nodeKey={nodeKey}
                            blockData={blockData}
                            onClose={onClose}
                            excalidrawRef={excalidrawRef}
                        />
                    </React.Suspense>
                </div>
            </div>
        </PortalAbsolute>
    );
}

function DrawingEditModalBody(props: {
    nodeKey: string;
    excalidrawRef: React.MutableRefObject<ExcalidrawImperativeAPI | undefined>;
    blockData: BlockDrawingData;
    onClose: (file: File | undefined) => void;
}): React.ReactElement {
    const { nodeKey, excalidrawRef, blockData, onClose } = props;

    const theme = useConfigurationTheme();

    const {
        blocks: { withDrawings },
    } = React.useContext(ContentEditorContext);
    assertIsDefined(withDrawings);
    const [generate, setGenerate] = React.useState(false);
    const toolbarRef = React.useRef<GenerateDrawingToolbarRef>(null);

    // Load asynchronously the component to render
    const { Excalidraw, useExcalidrawStyle } = excalidrawSuspense.read(null).default;
    useExcalidrawStyle();

    // Resolve to a file and load existing data if any
    const resolved = useContentReference(blockData.ref);
    const file = resolved.resolution?.extra?.file;
    const { result: initialData, error } = drawingDataCache.readSafe(file?.downloadURL || null);

    // Save the data as a file
    const onSaveAndClose = async () => {
        const excalidrawAPI = excalidrawRef.current;
        if (!excalidrawAPI) {
            onClose(undefined);
            return;
        }

        const elements = excalidrawAPI.getSceneElements();
        const files = excalidrawAPI.getFiles();

        if (initialData?.elements && !hasModifiedElements(initialData.elements, elements)) {
            // No changes, just close
            onClose(undefined);
            return;
        }

        const svg = await createDrawingSVG({ elements, files });
        onClose(createDrawingFile(svg, file?.name));
    };

    // Update the drawing with new data
    const onUpdateDrawing = (data: DrawingDocumentData) => {
        const excalidrawAPI = excalidrawRef.current;
        if (!excalidrawAPI) {
            return;
        }

        excalidrawAPI.updateScene({
            elements: data.elements,
        });
        excalidrawAPI.addFiles(Object.values(data.files));

        excalidrawAPI.scrollToContent(excalidrawAPI.getSceneElements(), {
            fitToContent: true,
        });
    };

    // Notify error to the user
    React.useEffect(() => {
        if (error) {
            toast.error('Error while loading drawing', error);
            throwIfNotExposable(error);
        }
    }, [error]);

    // Focus the toolbar when it appears
    React.useEffect(() => {
        if (generate && toolbarRef.current) {
            toolbarRef.current.focus();
        }
    }, [generate]);

    return (
        <div className="absolute inset-0 flex">
            <Excalidraw
                excalidrawAPI={(api) => {
                    excalidrawRef.current = api;
                }}
                initialData={{ ...initialData, scrollToContent: true }}
                theme={theme.color}
                UIOptions={{
                    canvasActions: {
                        export: false,
                        loadScene: false,
                        saveToActiveFile: false,
                        toggleTheme: false,
                        saveAsImage: false,
                        changeViewBackgroundColor: false,
                        clearCanvas: false,
                    },
                }}
                renderTopRightUI={() => {
                    return (
                        <div className="flex">
                            <CloseDrawingButton onClose={onSaveAndClose} />
                        </div>
                    );
                }}
            />
            <div className="absolute right-2 right-[280px] bottom-2 left-[280px] z-10 flex justify-center">
                {generate ? (
                    <GenerateDrawingToolbar
                        ref={toolbarRef}
                        nodeKey={nodeKey}
                        maxSuggestions={4}
                        closeDirection="bottom"
                        analyticsFrom="editor"
                        onSubmitDrawing={onUpdateDrawing}
                        onClose={() => setGenerate(false)}
                    />
                ) : (
                    <Button
                        kind="secondary"
                        label="Use AI to generate"
                        leadingIcon={Icon.Magic}
                        size="large"
                        className="m-2"
                        onPress={() => setGenerate(true)}
                    />
                )}
            </div>
        </div>
    );
}

/**
 * Check if an excalidraw content has been modified.
 */
function hasModifiedElements(
    before: readonly ExcalidrawElement[],
    after: readonly ExcalidrawElement[]
): boolean {
    if (before.length !== after.length) {
        return true;
    }

    return before.some((itemBefore, index) => {
        const itemAfter = after[index];
        return itemBefore.version !== itemAfter.version;
    });
}
